// src/components/opportunity/OpportunityRevisionsTab.tsx
//
// Revisions tab. Lists every revision (newest first), highlighting the
// active one. Slice 4 will add a "Reprint from this revision" button
// once the PDF generator lands; for now this is a read-only ledger so
// the user can see what's been saved.

import type {
  Opportunity,
  OpportunityRevision,
  OpportunityPricingSnapshot,
} from "@/types/opportunity";
import { formatOpportunityCurrency, formatOpportunityPct } from "@/lib/opportunity/formatCurrency";

export type OpportunityRevisionsTabProps = {
  opportunity: Opportunity;
  revisions: OpportunityRevision[];
  activeSnapshot: OpportunityPricingSnapshot | null;
};

export function OpportunityRevisionsTab(props: OpportunityRevisionsTabProps) {
  const { opportunity, revisions, activeSnapshot } = props;
  if (revisions.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
        No revisions saved yet. Open the <strong>Pricing</strong> tab and click "Save
        revision" to capture the first snapshot.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {revisions.map((r) => {
        const isActive = r.id === opportunity.active_revision_id;
        const snap = isActive ? activeSnapshot : null;
        return (
          <div
            key={r.id}
            className={
              "rounded-2xl border p-4 bg-white dark:bg-zinc-900 " +
              (isActive
                ? "border-indigo-300 ring-1 ring-indigo-200 dark:border-indigo-500/60 dark:ring-indigo-500/30"
                : "border-gray-200 dark:border-zinc-700")
            }
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-900 dark:text-zinc-100">
                  Revision #{r.revision_number}
                  {isActive ? (
                    <span className="ml-2 text-[11px] font-medium uppercase tracking-wide rounded-full px-2 py-0.5 bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">
                      Active
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-500 dark:text-zinc-400">
                  {formatTimestamp(r.created_at)}
                </div>
              </div>
              <span
                className="text-[11px] text-gray-400 dark:text-zinc-500"
                title="Reprint lands in Slice 4"
              >
                Reprint coming soon
              </span>
            </div>
            {r.notes ? (
              <p className="mt-2 text-sm text-gray-800 dark:text-zinc-200 whitespace-pre-wrap">
                {r.notes}
              </p>
            ) : null}
            {snap ? (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <Mini label="Total cost" value={formatOpportunityCurrency(snap.total_cost)} />
                <Mini
                  label="Sale price"
                  value={formatOpportunityCurrency(snap.total_sale_price)}
                />
                <Mini
                  label={`Margin (${formatOpportunityPct(snap.margin_pct)})`}
                  value={formatOpportunityCurrency(snap.margin_amount)}
                />
                <Mini
                  label={`Commission (${formatOpportunityPct(snap.commission_pct)})`}
                  value={formatOpportunityCurrency(snap.commission_amount)}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Mini(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 py-1 dark:border-zinc-700 dark:bg-zinc-800/70">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {props.label}
      </div>
      <div className="text-sm tabular-nums font-medium text-gray-900 dark:text-zinc-100">
        {props.value}
      </div>
    </div>
  );
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? "";
  return d.toLocaleString();
}
